import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAbsageBewerber } from '../../api/bewerber';
import { usePreviewBewerberEmail } from '../../api/email';
import { EmailEditor } from '../email/EmailEditor';
import { AbsageGrund, DokumentTyp, type AbsageBewerberDto, type Bewerber } from '../../types/bewerber';

interface AbsageModalProps {
  isOpen: boolean;
  onClose: () => void;
  bewerber: Bewerber;
  onSuccess?: () => void;
}

type Step = 'reason' | 'editor';

export function AbsageModal({
  isOpen,
  onClose,
  bewerber,
  onSuccess,
}: AbsageModalProps) {
  const absageMutation = useAbsageBewerber();
  const previewMutation = usePreviewBewerberEmail();
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('reason');
  const [editedHtml, setEditedHtml] = useState('');
  const [editedSubject, setEditedSubject] = useState('');

  // REGULAERE_ABSAGE is only allowed if the Bewerber is confirmed
  const canUseRegulaereAbsage = bewerber.isConfirmed;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<AbsageBewerberDto>({
    defaultValues: {
      // Default to DURCH_BEWERBER if REGULAERE_ABSAGE is not available
      grund: canUseRegulaereAbsage ? AbsageGrund.REGULAERE_ABSAGE : AbsageGrund.DURCH_BEWERBER,
      bemerkung: '',
    },
  });

  const selectedGrund = watch('grund');

  const getAbsageTemplateName = (): string => {
    const hasCV = bewerber.dokumente?.some(d => d.typ === DokumentTyp.LEBENSLAUF) ?? false;
    return hasCV ? 'bewerber-absage-regulaer' : 'bewerber-absage-ohne-daten';
  };

  const handleWeiter = async () => {
    if (!bewerber.email) {
      setError('Bewerber hat keine E-Mail-Adresse.');
      return;
    }

    setError(null);
    try {
      const templateName = getAbsageTemplateName();
      const result = await previewMutation.mutateAsync({
        bewerberId: bewerber.id,
        data: {
          templateName,
          data: {},
        },
      });
      setEditedHtml(result.data.html);
      setEditedSubject(result.data.subject);
      setStep('editor');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Fehler beim Laden der Vorschau';
      setError(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join(', ') : String(msg));
    }
  };

  const handleFormSubmit = async (data: AbsageBewerberDto) => {
    // For DURCH_BEWERBER: submit directly (no editor, no email)
    if (data.grund === AbsageGrund.DURCH_BEWERBER) {
      await submitAbsage(data);
      return;
    }

    // For REGULAERE_ABSAGE: go to editor step
    handleWeiter();
  };

  const handleSendAbsage = async () => {
    const data = getValues();
    await submitAbsage({
      ...data,
      emailBodyHtml: editedHtml,
      emailSubject: editedSubject,
    });
  };

  const submitAbsage = async (data: AbsageBewerberDto) => {
    setError(null);
    try {
      const result = await absageMutation.mutateAsync({
        id: bewerber.id,
        data,
      });

      if (result.meta.emailSent) {
        toast.success('Bewerber abgesagt und Absage-E-Mail gesendet');
      } else if (result.meta.emailSkipReason) {
        toast.warning(`Bewerber abgesagt. E-Mail nicht gesendet: ${result.meta.emailSkipReason}`);
      } else {
        toast.success('Bewerber erfolgreich abgesagt');
      }

      handleClose();
      onSuccess?.();
    } catch (err: any) {
      let errorMessage = 'Fehler beim Absagen des Bewerbers';
      const responseMessage = err?.response?.data?.message;
      if (Array.isArray(responseMessage)) {
        errorMessage = responseMessage.join(', ');
      } else if (typeof responseMessage === 'string') {
        errorMessage = responseMessage;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    setStep('reason');
    setEditedHtml('');
    setEditedSubject('');
    onClose();
  };

  if (!isOpen) return null;

  const bewerberName = `${bewerber.firstName} ${bewerber.lastName}`;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className={`relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:w-full ${step === 'editor' ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`}>
          {step === 'reason' && (
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Bewerber absagen
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      <span className="font-medium">{bewerberName}</span> wird auf den Status "Verloren" gesetzt.
                    </p>

                    {error && (
                      <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}

                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Grund der Absage *
                        </label>
                        <div className="space-y-2">
                          <label className="flex items-start">
                            <input
                              type="radio"
                              value={AbsageGrund.DURCH_BEWERBER}
                              {...register('grund', { required: true })}
                              className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                            />
                            <span className="ml-3">
                              <span className="block text-sm font-medium text-gray-900">
                                Durch Bewerber abgesagt
                              </span>
                              <span className="block text-sm text-gray-500">
                                Der Bewerber hat selbst abgesagt. Keine E-Mail wird gesendet.
                              </span>
                            </span>
                          </label>
                          <label className={`flex items-start ${!canUseRegulaereAbsage ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="radio"
                              value={AbsageGrund.REGULAERE_ABSAGE}
                              {...register('grund', { required: true })}
                              disabled={!canUseRegulaereAbsage}
                              className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 disabled:opacity-50"
                            />
                            <span className="ml-3">
                              <span className="block text-sm font-medium text-gray-900">
                                Reguläre Absage
                              </span>
                              <span className="block text-sm text-gray-500">
                                Absage-E-Mail wird an den Bewerber gesendet.
                              </span>
                            </span>
                          </label>
                        </div>
                      </div>

                      {!canUseRegulaereAbsage && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <div className="flex">
                            <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-blue-800">
                                Reguläre Absage nicht verfügbar
                              </p>
                              <p className="text-sm text-blue-700 mt-1">
                                Der Bewerber hat noch keine Bestätigungsmail erhalten. Eine reguläre Absage ist erst möglich, nachdem der Bewerber bestätigt wurde.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div>
                        <label htmlFor="bemerkung" className="block text-sm font-medium text-gray-700">
                          Bemerkung (optional)
                        </label>
                        <textarea
                          id="bemerkung"
                          rows={3}
                          {...register('bemerkung')}
                          placeholder="Zusätzliche Informationen zur Absage..."
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                        {errors.bemerkung && (
                          <p className="mt-1 text-sm text-red-600">{errors.bemerkung.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  disabled={absageMutation.isPending || previewMutation.isPending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {absageMutation.isPending
                    ? 'Wird abgesagt...'
                    : previewMutation.isPending
                    ? 'Wird geladen...'
                    : selectedGrund === AbsageGrund.REGULAERE_ABSAGE
                    ? 'Weiter'
                    : 'Absagen'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          )}

          {step === 'editor' && (
            <div>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Absage-E-Mail bearbeiten
                    </h3>
                    <p className="mt-2 text-sm text-gray-500">
                      E-Mail an <span className="font-medium">{bewerberName}</span> bearbeiten und senden
                    </p>

                    {error && (
                      <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}

                    <div className="mt-4">
                      <EmailEditor
                        renderedHtml={editedHtml}
                        renderedSubject={editedSubject}
                        onHtmlChange={setEditedHtml}
                        onSubjectChange={setEditedSubject}
                        isLoading={previewMutation.isPending}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleSendAbsage}
                  disabled={absageMutation.isPending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {absageMutation.isPending ? 'Wird abgesagt...' : 'Absagen & Senden'}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep('reason'); setError(null); }}
                  disabled={absageMutation.isPending}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Zurück
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
